<template>
  <div class="editor" :class="editorActiveClass">

    <template v-if="state.value !== 'delete'">

      <!-- Header -->
      <template v-if="setup === 'full'">
        <div class="editor-edit-bar" @click="send('TOGGLE_EDITOR')">
          <div class="font-bold" v-if="state.value === 'createNewContent' || state.value === 'idle'">Add new item</div>
          <div class="font-bold"  v-if="state.value === 'editContent'">Edit item</div>
          <div class="font-bold"  v-if="state.value === 'editTitle'">Edit title (should never show up)</div>
          <svg class="w-4 h-4">
            <use v-if="state.value === 'editContent' || state.value === 'createNewContent'" xlink:href="#cross" />
            <use v-else xlink:href="#notes" />
          </svg>
        </div>
      </template>

      <template v-if="setup === 'min-edit-only'">
        <div class="editor-edit-bar">
          <div class="text-xs tracking-wider uppercase text-pink-600 ml-auto" v-if="state.value === 'idle'">Edit options</div>
          <div class="font-bold" v-if="state.value === 'editTitle'">Edit note</div>
          <svg class="w-4 h-4" v-if="state.value === 'editTitle'" @click="send('TOGGLE_EDITOR_MIN')">
            <use xlink:href="#cross" />
          </svg>
        </div>
      </template>

      <template v-if="setup === 'min'">
        <div class="editor-edit-bar" @click="send('TOGGLE_EDITOR_MIN')">
          <div class="font-bold" v-if="state.value === 'createNewTitle' || state.value === 'idle'">Add new item</div>
          <div class="font-bold" v-if="state.value === 'editContent'">Edit item</div>
          <div class="font-bold" v-if="state.value === 'editTitle'">Edit title</div>
          <svg class="w-4 h-4">
            <use v-if="state.value === 'editContent' || state.value === 'createNewTitle'" xlink:href="#cross" />
            <use v-else xlink:href="#notes" />
          </svg>
        </div>
      </template>

      <!-- input fields && feedback field-->
      <div class="editor-wrapper" :class="editorVisibleClass">

        <input
          v-model="newTitle"
          @input="e => send({
            type: 'ENTER_TITLE',
            value: e.target.value
          })"
          @focus="clearValidationFeedback()"
          placeholder="A title"
          class="editor-input" />

        <textarea
          v-if="setup === 'full'"
          v-model="newContent"
          @input="e => send({
            type: 'ENTER_CONTENT',
            value: e.target.value
          })"
          @focus="clearValidationFeedback()"
          placeholder="Content"
          class="editor-textarea" />

        <div class="editor-feedback" v-if="state.matches('createNewContent') || state.matches('createNewTitle')">
          <span v-if="editorFeedback" class="editor-feedback">{{ editorFeedback }}</span>
          <button @click="send('SAVE')" class="btn btn-editor">Save</button>
        </div>

        <div class="editor-feedback mb-3" v-if="state.matches('editContent') || state.matches('editTitle')">
          <span v-if="editorFeedback" class="editor-feedback">{{ editorFeedback }}</span>
          <button @click="send('UPDATE')" class="btn btn-editor">Update</button>
        </div>

      </div>

    </template>

    <template v-if="state.value === 'delete'">
      <div class="editor-edit-bar">
        <div class="w-full" v-if="state.value === 'delete'">Deactivate Delete Mode</div>
      </div>
    </template>

  </div>
</template>

<script>
import { interpret } from 'xstate'
import { editorMachine } from '@/machines/editorMachine'

export default {
  name: 'Editor',
  props: {
    /**
     * @type {string}
     * "full": show input and textarea as well as individual header
     * "min":  show only input field and different header
     */
    setup: {
      type: String,
      required: true
    },
    /**
     * @type {string}
     * "mode" is sent to editorMachine and defines which state to call.
     * Possible values of "mode" are: edit, pre-edit, new, delete
     * "pre-edit" is fired, when the user changes between "edit" and "delete"
     * mode (e.g. in "Notes")
     * "delete" just closes and the editor and clears its text input fields
     */
    mode: {
      type: String,
      required: true
    },
    /**
     * @type {string}
     * The 'title' of the item to be edited or added
     */
    title: {
      type: String,
      required: false
    },
    content: {
      type: String,
      required: false
    },
    id: {
      type: Number,
      required: false
    },
    createdAt: {
      type: Number,
      required: false
    },
    feedback: {
      type: String,
      required: false
    },
    // css class that defines the height of the editor
    toggleClass: {
      type: String,
      required: false
    }
  },
  data () {
    return {
      //
      // ---- XSTATE
      //
      // Using xState machine, fired up in created() (Interpret the machine and store it in data)
      editorService: interpret(editorMachine.withConfig({
        actions: {
          assignOpenEditor: this.openEditor,
          assignCloseEditor: this.closeEditor,
          assignAddItem: this.addItem,
          assignUpdateItem: this.updateItem,
          assignSetFeedback: this.setFeedback
        }
      })),
      // Start with the machine's initial context
      context: editorMachine.context,
      // Start with the machine's initial state
      state: editorMachine.initialState,
      // Possible states
      possibleStates: ['createNewContent', 'createNewTitle', 'editContent', 'editTitle'],
      //
      // ----- PROPS TO LOCAL ATTRs
      //
      // Re-assign props to local params in order to be able to work with them (change them...)
      newTitle: this.title,
      newContent: this.content,
      editorFeedback: this.feedback,
      editorMode: this.mode
      // Update context with info from props
      // context.toggleClass = this.toggleClass
    }
  },
  computed: {
    editorActiveClass: {
      get () {
        let val = 'h-12'

        if (this.possibleStates.includes(this.state.value)) {
          val = 'h-1/2'

          if (this.setup === 'min' || this.setup === 'min-edit-only') {
            val = 'h-34'
          }
        }

        return val
      }
    },
    editorVisibleClass: {
      get () {
        let val = 'opacity-0'

        if (this.possibleStates.includes(this.state.value)) {
          val = 'opacity-100'
        }

        return val
      }
    }
  },
  mounted () {
    // Fire up the editorSerivce
    this.editorService
      .onTransition(state => {
        this.state = state
        // this.$l.log('Editor', 'mounted() - this.state', JSON.stringify(this.state, null, ' '))
      })
      .start()
  },
  destroyed () {
    // Stop the editorService again, when leaving
    this.editorService.stop()
  },
  methods: {
    //
    // ----- XState
    //
    /**
     * XState: Interpreter send() method shortcut
     *
     * @description "send" is a shortcut to the "editorService.send" method, which is
     * responsible for changing the state. 'itemObj': { id: this.id,
     * title: this.title, content: this.content }
     * @param {Object} event - Object { type: "[STATE]", value: [itemObj] }
     */
    send (event) {
      // console.log('\n\nEDITOR - SEND: event =', JSON.stringify(event, null, ' '))
      this.editorService.send(event)
    },
    openEditor () {
      // console.log('EditorMachine: openEditor invoked<br>')
      document.body.classList.add(this.toggleClass)
    },
    // In case the editor is open, but then "delete" is clicked,
    // the editor must be minimized and the fields be emptied again
    closeEditor () {
      // console.log('EditorMachine: closeEditor invoked<br>')
      document.body.classList.remove(this.toggleClass)
      // We must change the "mode" prop again, but can't do so fro inside the
      // child component; therefore emit an event and change the "mode" prop
      // from the parent
      // if (this.editorMode !== 'delete') {
      //   this.editorMode = 'new'
      // }
      this.$emit('onResetEditor')
      this.clearTextFields()
    },
    // Maybe these fields could go to "closeEditor"
    clearTextFields () {
      // console.log('EditorMachine: clearTextFields invoked<br>')
      this.newTitle = null
      this.newContent = null
      this.editorFeedback = null
    },
    updateTextFields () {
      // I actually thought "data..." would update automatically, when "props"
      // change... but it seems they do not...
      this.newTitle = this.title
      this.newContent = this.content
    },
    // Save new note
    addItem () {
      if (this.validateInput()) {
        this.editorFeedback = 'Successfully saved.'
        this.$emit('onAddItem', {
          title: this.newTitle,
          content: this.newContent,
          mode: this.mode,
          feedback: 'Successfully updated.'
        })
        /// Close editor
        // setTimeout(() => {
        //   this.send('IDLE')
        // }, 1000)
      }
    },
    updateItem () {
      // console.log('EDITOR: updateItem - this.newTitle = ' + this.newTitle + '<br>')
      if (this.validateInput()) {
        this.editorFeedback = 'Update successful.'
        this.$emit('onUpdateItem', {
          title: this.newTitle,
          content: this.newContent,
          id: this.id,
          createdAt: this.createdAt,
          mode: this.mode,
          feedback: 'Update successful.'
        })
      }
    },
    validateInput () {
      if (this.newTitle === '' || this.newTitle === null) {
        this.editorFeedback = 'Please enter a title.'
        return false
      }
      if (this.newTitle.length < 3) {
        this.editorFeedback = 'Title needs more than 2 characters.'
        return false
      }

      if (this.setup === 'full') {
        if (this.newContent === '' || this.newContent === null) {
          this.editorFeedback = 'Please add more content.'
          return false
        }
        if (this.newContent.length < 4) {
          this.editorFeedback = 'Text requires at least 5 characters.'
          return false
        }
      }

      if (this.newTitle === this.title && this.newContent === this.content) {
        // console.log('Special validation for edit mode: check if the texts changed at all' + '<br>')
        this.editorFeedback = 'Nothing changed.'
        return false
      }

      // this.editorFeedback = ''
      return true
    },
    clearValidationFeedback () {
      // As soon as the cursor is set into the input field again, the feedback
      // message – if there was any – disappears again.
      this.editorFeedback = null
    }
  },
  watch: {
    /**
     * "mode" controls the state machine of the editor. It is send in via a
     * prop and this watcher "spies" on its changes to initiate the appropriate
     * action. Possible modes: new, edit, pre-edit, delete
     * param {Number} newVal - The new value to which the "mode" just changed to
     * param {Number} oldVal - The old value of "mode"
     */
    mode (newVal, oldVal) {
      const item = {
        id: this.id,
        title: this.newTitle,
        content: this.newContent || null
      }

      switch (newVal) {
        case 'delete':
          // console.log('> delete to DELETE')
          this.send('DELETE')
          break

        case 'edit':
          // console.log('> edit to EDIT')
          this.updateTextFields()
          this.send({ type: 'EDIT', value: item })
          break

        case 'editTitle':
          // console.log('> editTitle to EDIT_TITLE')
          this.updateTextFields()
          this.send({ type: 'EDIT_TITLE', value: item })
          break

        case 'preEdit':
          // console.log('> preEdit to IDLE')
          this.send('IDLE')
          break

        case 'new':
          // console.log('> new to IDLE')
          this.send('IDLE')
          break

        case 'idle':
          // console.log('> idle to IDLE')
          this.send('IDLE')
          break

        default:
          // this.$l.error('Editor', 'watch:mode - Unhandled state!!!')
      }
    },
    /**
     * The "id" identifies an item that is sent to the editor in order to be
     * changed/edited.
     * A user might click on an item with the intend to edit it, but can change
     * his mind again and instead click on another item. The only way to detect
     * this change in a Vue child component is via a watcher and an unique
     * attribute of that very item, in this case its "id". This way we can
     * easily detect that the item to be edited has changed and update the
     * text fields accordingly.
     * param {Number} newVal - The new value to which the "id" just changed to
     * param {Number} oldVal - The old value of "id"
     */
    id (newVal, oldVal) {
      this.updateTextFields()

      const item = {
        id: this.id,
        title: this.title,
        content: this.content
      }

      this.send({
        type: 'EDIT',
        value: item
      })
    },
    feedback: function (newVal, oldVal) {
      this.feedback = newVal
    }
  }
}
</script>

<style lang="postcss" scoped>
.editor {
  @apply relative flex flex-col flex-shrink-0 content-between w-full mt-auto overflow-hidden
}
.editor-edit-bar {
  @apply relative flex flex-row items-center justify-between mb-3 select-none pt-4 border-t border-pink-700 cursor-pointer
}
.editor-wrapper {
  @apply relative flex flex-col flex-grow overflow-hidden p-1 transition duration-700 ease-in-out
}
.editor-input {
  @apply w-full mb-2 rounded-md p-2 text-gray-700
}
.editor-textarea {
  @apply w-full mb-2 rounded-md p-2 text-gray-700 flex-grow
}
.editor-feedback {
  @apply flex flex-row items-center justify-between w-full select-none
}
.editor-feedback-txt {
  @apply text-pink-200 mr-1 leading-tight
}

.btn-editor {
  @apply ml-auto
}
.btn-editor:hover {
  @apply bg-pink-600
}
</style>
